<template>
  <b-modal
    v-model="store.showModal"
    title="Syarat & Ketentuan Penggunaan AI (DekaAdvice)"
    size="lg"
    modal-class="ai-consent-modal"
    no-close-on-backdrop
    no-close-on-esc
    hide-header-close
    centered
    body-class="ai-consent-body p-0"
    footer-class="d-block px-1 py-75"
    @shown="onShown"
    @hidden="resetState"
  >
    <div ref="consentScroll" class="ai-consent-scroll px-1 py-75" @scroll="onScroll">
      <b-alert show variant="warning" class="mb-75 py-50 px-75">
        <div class="alert-body d-flex align-items-start">
          <feather-icon icon="AlertTriangleIcon" class="mr-50 mt-25" size="18" />
          <span class="small">
            <strong>Peringatan Penting:</strong>
            Fitur AI Medical Support Advisor ini masih dalam tahap pengembangan (beta).
            Material yang dihasilkan bersifat eksperimental, dapat mengandung ketidaktepatan,
            dan belum melalui validasi klinis penuh.
          </span>
        </div>
      </b-alert>

      <h6 class="mb-50">1. Tujuan Penggunaan AI</h6>
      <p class="small mb-50">
        Sistem AI ini digunakan hanya sebagai alat bantu bagi tenaga kesehatan dalam proses
        pelayanan medis, termasuk namun tidak terbatas pada: penyusunan catatan medis (SOAP),
        saran diagnosis awal, referensi ICD-10, pemeriksaan interaksi obat, dukungan
        administrasi dan klaim, serta penggunaan umum lainnya.
      </p>
      <p class="small mb-75">
        AI <strong>bukan alat pengganti tenaga medis</strong> dan
        <strong>tidak digunakan sebagai pengambil keputusan klinis utama</strong>.
      </p>

      <h6 class="mb-50">2. Kewajiban Pengawasan oleh Tenaga Medis</h6>
      <p class="small mb-75">
        Setiap hasil atau rekomendasi yang dihasilkan AI
        <strong>wajib ditinjau, diverifikasi, dan disetujui</strong> oleh tenaga medis yang berwenang.
        Tidak boleh langsung digunakan tanpa evaluasi klinis mendalam.
        <strong>Tanggung jawab akhir sepenuhnya berada pada tenaga medis</strong>, bukan pada sistem AI.
      </p>

      <h6 class="mb-50">3. AI Tidak Melakukan Tindakan Medis Mandiri</h6>
      <p class="small mb-25">Sistem AI (dalam status beta):</p>
      <ul class="small mb-75 pl-1">
        <li>Tidak menetapkan diagnosis final</li>
        <li>Tidak menentukan terapi</li>
        <li>Tidak meresepkan obat</li>
        <li>Tidak menggantikan keputusan dokter</li>
      </ul>

      <h6 class="mb-50">4. Keterbatasan Sistem</h6>
      <p class="small mb-25">Pengguna memahami bahwa:</p>
      <ul class="small mb-75 pl-1">
        <li>AI dapat menghasilkan kesalahan atau ketidaktepatan (risiko lebih tinggi pada fase beta)</li>
        <li>AI bergantung pada kualitas data yang dimasukkan</li>
        <li>AI tidak selalu mencerminkan pedoman klinis terbaru di Indonesia</li>
      </ul>

      <h6 class="mb-50">5. Kepatuhan terhadap Regulasi Indonesia</h6>
      <p class="small mb-75">
        Penggunaan AI ini harus mematuhi standar pelayanan medis yang berlaku, peraturan dari
        Kementerian Kesehatan RI, ketentuan etika profesi kedokteran dan tenaga kesehatan,
        serta regulasi perlindungan data dan rekam medis.
      </p>

      <h6 class="mb-50">6. Keselamatan Pasien</h6>
      <p class="small mb-75">
        AI <strong>tidak boleh digunakan sebagai satu-satunya dasar</strong> dalam pengambilan
        keputusan yang berdampak pada keselamatan pasien, tindakan invasif, atau keputusan
        kritis/darurat. Prioritas utama adalah <strong>keselamatan dan kepentingan pasien</strong>.
      </p>

      <h6 class="mb-50">7. Perlindungan Data dan Kerahasiaan</h6>
      <p class="small mb-75">
        Data pasien yang digunakan dalam sistem AI hanya untuk keperluan pelayanan kesehatan,
        harus dijaga kerahasiaannya, dan tidak boleh digunakan di luar tujuan medis dan
        administratif yang sah.
      </p>

      <h6 class="mb-50">8. Tidak Digunakan untuk Kondisi Gawat Darurat</h6>
      <p class="small mb-75">
        Sistem AI <strong>tidak dirancang untuk penggunaan darurat</strong> tanpa keterlibatan
        langsung tenaga medis. Keputusan dalam kondisi gawat darurat
        <strong>harus diambil oleh tenaga medis secara langsung</strong>.
      </p>

      <h6 class="mb-50">9. Perubahan Sistem</h6>
      <p class="small mb-75">
        Fungsi dan kemampuan AI dapat berubah seiring waktu. Pengguna bertanggung jawab untuk
        memahami keterbatasan sistem pada versi yang digunakan.
      </p>

      <h6 class="mb-50">10. Persetujuan Penggunaan</h6>
      <p class="small mb-0">
        Dengan menggunakan sistem AI ini, pengguna menyatakan bahwa: memahami AI hanya
        sebagai alat bantu, tetap menggunakan pertimbangan profesional, dan bertanggung
        jawab penuh atas setiap keputusan medis.
      </p>
    </div>

    <template #modal-footer>
      <div v-if="!hasReachedBottom" class="text-center w-100 mb-50">
        <small class="text-muted">
          <feather-icon icon="ArrowDownIcon" size="14" class="mr-25" />
          Scroll ke bawah untuk membaca seluruh syarat &amp; ketentuan
        </small>
      </div>
      <div class="d-flex align-items-center justify-content-between w-100">
        <b-form-checkbox
          v-model="agreed"
          :disabled="!hasReachedBottom"
          class="custom-control-primary mr-auto"
        >
          <span class="font-weight-bolder" style="font-size: 0.9rem;">
            Saya telah membaca dan menyetujui S&amp;K AI (DekaAdvice)
          </span>
        </b-form-checkbox>
        <div class="d-flex flex-shrink-0 ml-50" style="gap: 0.5rem;">
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="dismiss"
          >
            Batal
          </b-button>
          <b-button
            variant="primary"
            size="sm"
            :disabled="!agreed || submitting"
            @click="submit"
          >
            <b-spinner v-if="submitting" small class="mr-25" />
            Setuju &amp; Lanjutkan
          </b-button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { defineComponent, ref } from '@vue/composition-api'
import { useAiConsentStore } from '@/pinia/aiConsent'

export default defineComponent({
  setup() {
    const store = useAiConsentStore()
    const agreed = ref(false)
    const submitting = ref(false)
    const hasReachedBottom = ref(false)
    const consentScroll = ref(null)

    function onScroll() {
      const el = consentScroll.value
      if (!el) return
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 30
      if (atBottom) {
        hasReachedBottom.value = true
      }
    }

    function onShown() {
      // Check if content is short enough that no scroll is needed
      const el = consentScroll.value
      if (el && el.scrollHeight <= el.clientHeight + 30) {
        hasReachedBottom.value = true
      }
    }

    function resetState() {
      agreed.value = false
      hasReachedBottom.value = false
    }

    async function submit() {
      if (!agreed.value) return
      submitting.value = true
      try {
        await store.submitConsent()
      } catch (e) {
        // Error handled in store
      } finally {
        submitting.value = false
        agreed.value = false
      }
    }

    function dismiss() {
      agreed.value = false
      store.dismissConsent()
    }

    return {
      store,
      agreed,
      submitting,
      hasReachedBottom,
      consentScroll,
      submit,
      dismiss,
      onScroll,
      onShown,
      resetState,
    }
  },
})
</script>

<style>
/* Unscoped — override global kunjungan modal styles for this specific modal */
.ai-consent-modal .modal-dialog {
  max-width: 800px !important;
  max-height: 90vh !important;
}

.ai-consent-modal .modal-content {
  max-height: 85vh !important;
}

.ai-consent-modal .modal-body.ai-consent-body {
  padding: 0 !important;
  overflow: hidden !important;
  height: auto !important;
  min-height: unset !important;
  max-height: unset !important;
}
</style>

<style scoped>
.ai-consent-scroll {
  max-height: 55vh;
  overflow-y: auto;
  font-size: 0.875rem;
  line-height: 1.5;
}
</style>
